import {
    Box, IconButton, InputAdornment, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Tooltip, Typography, Autocomplete,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import LedgerLogo from "../../assets/Images/ledger.png"
import { useNavigate } from 'react-router-dom'
import { ApiGetLedger } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import { useDateRange } from '../../hooks/useDateRange'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'
import Ledger1 from '../../assets/Images/ledger1.png'
import { usePersistedState } from '../../hooks/userPersistedState'

interface LedgerEntry {
    _id: string
    type: 'DEBIT' | 'CREDIT'
    referenceType: string
    account: string
    counterparty: string
    amount: number
    executedAmount: number
    orderFee: number
    platformFee: number
    orderSide: string | null
    orderType: string | null
    sfoxStatus: string | null
    currency: string
    description: string
    status: 'PENDING' | 'COMPLETED' | 'REVERSED' | 'FAILED'
    runningBalance: number
    createdAt: string
    userId: {
        _id: string
        firstName: string
        lastName: string
        email: string
    }
    linkedEntryId?: {
        type: string
        account: string
        amount: number
        status: string
    } | null
}

// interface Summary {
//     deposits: number
//     withdrawals: number
//     totalIn: number
//     totalOut: number
//     netBalance: number
//     pendingDeposits: { count: number; total: number }
//     pendingWithdrawals: { count: number; total: number }
//     totalOrderFees: number
//     adjustmentTotal: number
//     adjustmentFees: number
// }


const TYPE_OPTIONS = ['DEBIT', 'CREDIT']
const STATUS_OPTIONS = ['PENDING', 'COMPLETED', 'REVERSED', 'FAILED']
const FIAT_CURRENCIES = ['USD', 'AUD']

const isFiatCurrency = (currency: string) =>
    FIAT_CURRENCIES.includes(currency?.toUpperCase())

const formatCryptoAmount = (amount: number) =>
    amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })

const getEntryAmount = (entry: LedgerEntry) =>
    (entry.referenceType === 'ORDER' || entry.referenceType === 'ADJUSTMENT')
        // ? entry.executedAmount
        ? entry.amount
        : entry.amount

const formatCurrencyAmount = (entry: LedgerEntry) => {
    const value = getEntryAmount(entry)
    const sign = entry.type === 'CREDIT' ? '+' : '-'
    return isFiatCurrency(entry.currency)
        ? `${sign}$${formatAmount(value)}`
        : `${sign}${formatCryptoAmount(value)} ${entry.currency}`
}


const formatAmount = (amount: number) =>
    amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatDate = (date: string) =>
    new Date(date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

const formatTime = (date: string) =>
    new Date(date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const getStatusChip = (status: string) => {
    const map: Record<string, { color: string; bg: string; border: string }> = {
        COMPLETED: { color: '#10b981', bg: '#10b98120', border: '#10b981' },
        PENDING: { color: '#f59e0b', bg: '#f59e0b20', border: '#f59e0b' },
        REVERSED: { color: '#3b82f6', bg: '#3b82f620', border: '#3b82f6' },
        FAILED: { color: '#ef4444', bg: '#ef444420', border: '#ef4444' },
    }
    const s = map[status] || { color: '#9a9a9a', bg: '#9a9a9a20', border: '#9a9a9a' }
    return (
        <Box sx={{
            display: 'inline-block', px: 1.5, py: 0.4,
            borderRadius: '5px', fontSize: '11px', fontWeight: 600,
            color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`,
            whiteSpace: 'nowrap'
        }}>
            {status}
        </Box>
    )
}

const getRefChip = (ref: string) => (
    <Box sx={{
        display: 'inline-block', px: 1.5, py: 0.4, borderRadius: '5px',
        fontSize: '10px', fontWeight: 500,
        color: '#9a9a9a', backgroundColor: '#2a2a2a', border: '1px solid #3f3f3f',
        whiteSpace: 'nowrap'
    }}>
        {ref}
    </Box>
)


const SystemLedger: React.FC = () => {
    const navigate = useNavigate()

    const [entries, setEntries] = useState<LedgerEntry[]>([])
    // const [summary, setSummary] = useState<Summary | null>(null)
    const [search, setSearch] = usePersistedState('systemLedger:search', '')
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [type, setType] = usePersistedState('systemLedger:type', '')
    const [status, setStatus] = usePersistedState('systemLedger:status', '')
    const [page, setPage] = usePersistedState('systemLedger:page', 0)
    const [rowsPerPage, setRowsPerPage] = usePersistedState('systemLedger:rowsPerPage', 10)
    const [totalRows, setTotalRows] = useState(0)
    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({
        open: false, message: '',
    })
    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('system-ledger', 'all')

    const fetchTransactions = useCallback(async () => {
        try {
            const dateQuery = buildQuery()
            if (dateQuery === null) return

            const params = new URLSearchParams()
            if (type) params.append('type', type)
            if (status) params.append('status', status)
            if (debouncedSearch) params.append('search', debouncedSearch)
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))
            params.append('account', 'PLATFORM')
            if (dateQuery) {
                new URLSearchParams(dateQuery).forEach((value, key) => params.append(key, value))
            }
            const res = await ApiGetLedger(`?${params.toString()}`)
            if (res?.status) {
                setEntries(res.data.entries)
                // setSummary(res.data.summary)
                setTotalRows(res.data.pagination.total)
            }
        } catch (error: any) {
            console.error('Error fetching system ledger entries:', error)
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || 'Failed to fetch system ledger entries',
            })
        }
    }, [type, status, debouncedSearch, page, rowsPerPage, buildQuery])

    useEffect(() => { fetchTransactions() }, [fetchTransactions])

    // const summaryCards = summary ? [
    //     { label: 'Total In', value: `$${formatAmount(summary.totalIn)}`, color: '#10b981' },
    //     { label: 'Total Out', value: `$${formatAmount(summary.totalOut)}`, color: '#ef4444' },
    //     { label: 'Net Balance', value: `$${formatAmount(summary.netBalance)}`, color: summary.netBalance >= 0 ? '#10b981' : '#ef4444' },
    //     { label: 'Pending Deposits', value: `$${formatAmount(summary.pendingDeposits.total)}`, sub: `${summary.pendingDeposits.count} entries`, color: '#f59e0b' },
    //     { label: 'Pending Withdrawals', value: `$${formatAmount(summary.pendingWithdrawals.total)}`, sub: `${summary.pendingWithdrawals.count} entries`, color: '#ef4444' },
    //     { label: 'Order Fees', value: `$${formatAmount(summary.totalOrderFees)}`, color: '#f59e0b' },
    //     { label: 'Selldown', value: `$${formatAmount(summary.adjustmentTotal)}`, sub: `fees: $${formatAmount(summary.adjustmentFees)}`, color: '#ef4444' },
    // ] : []

    const prevSearchRef = React.useRef(search)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            if (search !== prevSearchRef.current) {
                setPage(0);
            }
            prevSearchRef.current = search
        }, 400);
        return () => clearTimeout(timer);
    }, [search, setPage]);

    const prevDateFiltersRef = React.useRef({ range, customStart, customEnd })
    useEffect(() => {
        const prev = prevDateFiltersRef.current
        const changed =
            prev.range !== range ||
            (prev.customStart?.valueOf() !== customStart?.valueOf()) ||
            (prev.customEnd?.valueOf() !== customEnd?.valueOf())
        if (changed) setPage(0)
        prevDateFiltersRef.current = { range, customStart, customEnd }
    }, [range, customStart, customEnd, setPage])
    return (
        <div className='common-layout flex-column'>

            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <Box className="p-2 bg-light-white border-10">
                        <img src={Ledger1} alt="Ledger" style={{ width: '28px', height: '28px' }} />
                    </Box>
                    <Typography className='fs-24 text-white fw-medium'>System Ledger</Typography>
                </Box>
                <Typography className='fs-24 text-white fw-medium'>
                    Total: {totalRows}
                </Typography>
            </Box>

            {/* {summary && (
                <Grid container spacing={2} mb={4}>
                    {summaryCards.map((card) => (
                        <Grid key={card.label} size={{ xs: 12, sm: 6, md: 4, lg: 12 / summaryCards.length }}>
                            <Box className="border-10 p-3 px-4 bg-dark-color" sx={{ height: '100%' }}>
                                <Typography fontSize={11} color="#6f6f6f" textTransform="uppercase" mb={0.5}>
                                    {card.label}
                                </Typography>
                                <Typography fontSize={18} fontWeight={700} color={card.color}>
                                    {card.value}
                                </Typography>
                                {card.sub && (
                                    <Typography fontSize={10} color="#6f6f6f" mt={0.5}>{card.sub}</Typography>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )} */}

            <DateRangeFilter
                range={range}
                onRangeChange={handleRangeChange}
                customStart={customStart}
                onCustomStartChange={setCustomStart}
                customEnd={customEnd}
                onCustomEndChange={setCustomEnd}
            />

            <Box className="bg-dark-color border-10 p-4 mt-2">

                <Box className="d-flex align-items-center gap-3 mb-4" sx={{ flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Search description or email..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); }}
                        size="small"
                        sx={searchFieldStyle}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className='color-primary' />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FilterAutocomplete
                        value={type}
                        options={TYPE_OPTIONS}
                        placeholder="Dr / Cr"
                        width={140}
                        onChange={(v) => { setType(v); }}
                    />

                    <FilterAutocomplete
                        value={status}
                        options={STATUS_OPTIONS}
                        placeholder="Status"
                        width={160}
                        onChange={(v) => { setStatus(v); }}
                    />

                    {(type || status) && (
                        <Box className="d-flex gap-2 align-items-center ms-auto">
                            {[
                                type && { label: type, clear: () => setType('') },
                                status && { label: status, clear: () => setStatus('') },
                            ].filter(Boolean).map((chip: any) => (
                                <Box
                                    key={chip.label}
                                    onClick={chip.clear}
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                        px: 1.5, py: 0.4, borderRadius: '5px', cursor: 'pointer',
                                        fontSize: '11px', fontWeight: 600,
                                        color: '#ff6a3d', backgroundColor: '#ff6a3d18', border: '1px solid #ff6a3d55',
                                        '&:hover': { backgroundColor: '#ff6a3d30' },
                                    }}
                                >
                                    {chip.label} ✕
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                <TableContainer sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                    <Table
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-root': { borderBottom: 'none' },
                            '& .MuiTableRow-root': { borderBottom: 'none' },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {[
                                    'No', 'Date & Time', 'User', 'Type',
                                    'Reference', 'Account → Counterparty',
                                    'Amount', 'Description', 'Status', 'Actions'
                                ].map((head) => (
                                    <TableCell
                                        key={head}
                                        className='color-primary'
                                        sx={{
                                            backgroundColor: '#241815', color: '#ff6a3d',
                                            position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {entries.length > 0 ? entries.map((entry, index) => (
                                <TableRow
                                    key={entry._id}
                                    sx={{
                                        '&:hover': { backgroundColor: '#1a1210' },
                                        transition: 'background-color 0.15s',
                                    }}
                                >
                                    <TableCell className='text-white py-1' sx={{ fontSize: 13, color: '#6f6f6f' }}>
                                        {page * rowsPerPage + index + 1}
                                    </TableCell>

                                    <TableCell className='py-1' sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography fontSize={12} color="#fff">{formatDate(entry.createdAt)}</Typography>
                                        <Typography fontSize={10} color="#6f6f6f">{formatTime(entry.createdAt)}</Typography>
                                    </TableCell>

                                    <TableCell className='py-1' sx={{ minWidth: 160 }}>
                                        <Typography fontSize={12} color="#fff" fontWeight={500}>
                                            {entry.userId?.firstName} {entry.userId?.lastName}
                                        </Typography>
                                        <Typography fontSize={10} color="#6f6f6f" sx={{ wordBreak: 'break-all' }}>
                                            {entry.userId?.email}
                                        </Typography>
                                    </TableCell>

                                    <TableCell className='py-1'>
                                        <Box sx={{
                                            display: 'inline-block', px: 1.5, py: 0.4, borderRadius: '5px',
                                            fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
                                            color: entry.type === 'CREDIT' ? '#10b981' : '#ef4444',
                                            backgroundColor: entry.type === 'CREDIT' ? '#10b98120' : '#ef444420',
                                            border: `1px solid ${entry.type === 'CREDIT' ? '#10b981' : '#ef4444'}`,
                                        }}>
                                            {entry.type}
                                        </Box>
                                    </TableCell>

                                    <TableCell className='py-1'>{getRefChip(entry.referenceType)}</TableCell>

                                    <TableCell className='py-1' sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                        <span style={{ color: '#9a9a9a' }}>{entry.account}</span>
                                        <span style={{ color: '#3f3f3f', margin: '0 4px' }}>→</span>
                                        <span style={{ color: '#9a9a9a' }}>
                                            {typeof entry.counterparty === 'object' && entry.counterparty
                                                ? `${(entry.counterparty as any).firstName || ''} ${(entry.counterparty as any).lastName || ''}`.trim()
                                                : entry.counterparty}
                                        </span>
                                    </TableCell>

                                    <TableCell className='py-1' sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography
                                            fontSize={13} fontWeight={700}
                                            color={entry.type === 'CREDIT' ? '#10b981' : '#ef4444'}
                                        >
                                            {formatCurrencyAmount(entry)}
                                        </Typography>
                                        {(entry.referenceType === 'ORDER' || entry.referenceType === 'ADJUSTMENT') && entry.orderFee > 0 && (
                                            <Typography fontSize={10} color="#9a9a9a">
                                                fee: ${formatAmount(entry.orderFee + entry.platformFee)}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell className='py-1' sx={{
                                        color: '#9a9a9a', fontSize: '12px',
                                        maxWidth: 220, overflow: 'hidden',
                                        textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                    }}>
                                        <Tooltip title={entry.description} arrow>
                                            <span>{entry.description}</span>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell className='py-1'>{getStatusChip(entry.status)}</TableCell>

                                    <TableCell className='py-1'>
                                        <Tooltip title="View User Ledger" arrow>
                                            <IconButton
                                                className='color-primary'
                                                size="small"
                                                onClick={() => navigate('/admin/users/ledgers', {
                                                    state: {
                                                        userId: entry.userId?._id,
                                                        firstName: entry.userId?.firstName,
                                                        lastName: entry.userId?.lastName,
                                                        email: entry.userId?.email,
                                                        account: "USER"
                                                    }
                                                })}
                                            >
                                                <img src={LedgerLogo} height={"25px"} alt="Ledger" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                        <ReceiptLongIcon sx={{ fontSize: 40, color: '#3f3f3f', mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography color="#6f6f6f" fontSize={14}>No system ledger entries found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    className='bg-dark-color'
                    component="div"
                    count={totalRows}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton
                    showLastButton
                    sx={paginationStyle}
                />
            </Box>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
        </div>
    )
}

export default SystemLedger


interface FilterAutocompleteProps {
    value: string
    options: string[]
    placeholder: string
    width: number
    onChange: (v: string) => void
}

const FilterAutocomplete: React.FC<FilterAutocompleteProps> = ({ value, options, placeholder, width, onChange }) => (
    <Autocomplete
        size="small"
        value={value}
        disableClearable={!value}
        onChange={(_, v) => onChange(v || '')}
        options={options}
        renderInput={(params) => (
            <TextField
                {...params}
                placeholder={placeholder}
                sx={autocompleteFieldStyle}
                InputProps={{
                    ...params.InputProps,
                    sx: {
                        color: '#fff',
                        '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' },
                    },
                }}
            />
        )}
        sx={{ width }}
        PaperComponent={({ children }) => (
            <Box
                className="bg-dark-color"
                sx={{
                    color: '#fff',
                    '& .MuiAutocomplete-option': {
                        '&:hover': { bgcolor: '#241815' },
                        "&[aria-selected='true']": { bgcolor: '#241815' },
                    },
                }}
            >
                {children}
            </Box>
        )}
    />
)


const searchFieldStyle = {
    width: 320, background: 'transparent', borderRadius: '7px',
    border: '1px solid #898989', transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff' },
    '& input::placeholder': { color: '#999', opacity: 1 },
}

const autocompleteFieldStyle = {
    '& .MuiInputBase-root': { background: 'transparent', borderRadius: '8px', border: '1px solid #898989', transition: 'border-color 0.3s' },
    '& .MuiInputBase-root:hover': { border: '1px solid #ff6a3d' },
    '& .MuiInputBase-root.Mui-focused': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input::placeholder': { color: '#999', opacity: 1 },
}

const paginationStyle = {
    color: '#898989', borderTop: '1px solid #2a2a2a',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#898989', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#898989', margin: 0 },
    '& .MuiSelect-select': { color: '#898989' },
    '& .MuiSelect-icon': { color: '#898989' },
    '& .MuiIconButton-root': { color: '#898989' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#555' },
}